/**
 * Builds the Jungian analyst prompt from survey answers for use with Replicate API.
 * Survey answers shape: { gender, looking_for, music[], movies[] }
 */

import { generateJungianProfile, generatePartnerCutoutNanoBanana } from '../services/firebaseApiService';

const formatList = (arr) => {
  if (!arr || !Array.isArray(arr) || arr.length === 0) return '';
  if (arr.length === 1) return String(arr[0]);
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  const last = arr[arr.length - 1];
  const rest = arr.slice(0, -1);
  return `${rest.join(', ')}, and ${last}`;
};

/**
 * Fills the Jungian analyst form with survey answers.
 * @param {Object} answers - Survey answers (e.g. from survey_responses or surveyAnswers state)
 * @returns {string} Prompt text ready to feed into the Replicate API.
 */
export function buildJungianPrompt(answers) {
  if (!answers || typeof answers !== 'object') {
    return '';
  }

  const gender = answers.gender ?? '';
  const lookingFor = answers.looking_for ?? '';
  const music = formatList(answers.music);
  const movies = formatList(answers.movies);

  return `You're a Jungian analyst helping your client understand what their subconscious is telling them about their potential romantic partner.

Your client is ${gender} and is looking for ${lookingFor}. Your client is 22 years old. Your client likes such musicians as ${music} and such movies as ${movies}.

Based on this information, profile the following characteristics of your client's potential romantic partner.

You must reply with exactly 4 lines in this format. Use only these lines, no other text, no explanation, no preamble. Each line must start with the number and a period, then the label, then the value as shown:

1. ethnicity: [e.g. asian]
2. clothing: [e.g. oversized graphic tee, baggy cargo pants, platform boots]
3. style: [e.g. artsy]
4. body type: [e.g. rounded, plus size]

Example output (use this structure, replace only the content inside brackets):
1. ethnicity: [asian]
2. clothing: [oversized graphic tee, baggy cargo pants, platform boots]
3. style: [artsy]
4. body type: [plus size]`;
}

/** Valid personality types for the profile chart (must match PersonalityChart PERSONALITY_KEYS) */
const PERSONALITY_TYPES = ['individualist', 'peacemaker', 'enthusiast', 'helper', 'challenger', 'investigator', 'perfectionist', 'loyalist', 'achiever'];

/**
 * Parses the "5. personality: ..." line from Jungian analysis text.
 * Handles: personality: [individualist], personality: ["individualist"], personality: "individualist", 5. personality: value, etc.
 * @param {string} analysis - Full Jungian analysis text from Gemini
 * @returns {string|null} Normalized personality key (e.g. "Achiever") or null if not found/invalid
 */
export function parsePersonalityFromAnalysis(analysis) {
  if (!analysis || typeof analysis !== 'string') return null;
  let match;
  // Bracket+quote: personality: ["individualist"] or 5. personality: ["individualist"]
  match = analysis.match(/personality\s*:\s*\[\s*"(\w+)"\s*\]/i) ||
    analysis.match(/(?:^|\n)\s*5\.?\s*personality\s*:\s*\[\s*"(\w+)"\s*\]/im);
  if (match) return normalizePersonality(match[1]);
  // Bracket only: personality: [individualist] or 5. personality: [individualist]
  match = analysis.match(/personality\s*:\s*\[\s*(\w+)\s*\]/i) ||
    analysis.match(/(?:^|\n)\s*5\.?\s*personality\s*:\s*\[\s*(\w+)\s*\]/im);
  if (match) return normalizePersonality(match[1]);
  // Quote only: personality: "individualist" or 5. personality: "individualist"
  match = analysis.match(/personality\s*:\s*"(\w+)"/i) ||
    analysis.match(/(?:^|\n)\s*5\.?\s*personality\s*:\s*"(\w+)"/im);
  if (match) return normalizePersonality(match[1]);
  // No brackets/quotes: 5. personality: value or personality: value
  match = analysis.match(/5\.\s*personality\s*:\s*(\w+)/i) ||
    analysis.match(/personality\s*:\s*(\w+)/i);
  if (match) return normalizePersonality(match[1]);
  return null;
}

function normalizePersonality(value) {
  const trimmed = (value || '').trim().toLowerCase();
  const found = PERSONALITY_TYPES.find((t) => t.toLowerCase() === trimmed);
  return found ? found.charAt(0).toUpperCase() + found.slice(1) : null;
}

/**
 * Parses the "6. nickname: ..." line from Jungian analysis text.
 * Handles: nickname: ["The Nomad"], nickname: "The Muse", 6. nickname: The One, nickname: [The Dreamer], etc.
 * @param {string} analysis - Full Jungian analysis text from Gemini
 * @returns {string|null} Nickname (e.g. "The One", "The Dreamer") or null if not found
 */
export function parseNicknameFromAnalysis(analysis) {
  if (!analysis || typeof analysis !== 'string') return null;
  // Bracket+quote: nickname: ["The Nomad"] or 6. nickname: ["The Nomad"]
  let match = analysis.match(/nickname\s*:\s*\[\s*"([^"]*)"\s*\]/i) ||
    analysis.match(/(?:^|\n)\s*6\.?\s*nickname\s*:\s*\[\s*"([^"]*)"\s*\]/im);
  if (match) return (match[1] || '').trim() || null;
  // Quote only: nickname: "The Muse" or 6. nickname: "The Muse"
  match = analysis.match(/nickname\s*:\s*"([^"]*)"/i) ||
    analysis.match(/(?:^|\n)\s*6\.?\s*nickname\s*:\s*"([^"]*)"/im);
  if (match) return (match[1] || '').trim() || null;
  // Unquoted: nickname: [value] or nickname: value
  match = analysis.match(/nickname\s*:\s*\[\s*([^\]\n]+)\s*\]/i) ||
    analysis.match(/nickname\s*:\s*([^\s\n\[\]]+)/i);
  if (!match) return null;
  const value = (match[1] || '').trim().replace(/^["']|["']$/g, '').trim();
  return value || null;
}

const PERSONALITY_KEYS_CAPITALIZED = PERSONALITY_TYPES.map((t) => t.charAt(0).toUpperCase() + t.slice(1));

/**
 * Returns a random dominant personality type (for fallback when analysis has none).
 * @returns {string} One of the 9 personality keys (e.g. "Achiever")
 */
export function getRandomDominantPersonality() {
  return PERSONALITY_KEYS_CAPITALIZED[Math.floor(Math.random() * PERSONALITY_KEYS_CAPITALIZED.length)];
}

/**
 * Fetches Jungian partner profile from Replicate (Gemini via Firebase Cloud Function).
 * @param {Object} answers - Survey answers
 * @param {string} [userTier='free'] - User tier for rate limiting
 * @returns {Promise<string|null>} Analysis text (ethnicity, clothing, style, body type) or null on empty prompt; throws on API error.
 */
export async function fetchJungianAnalysis(answers, userTier = 'free') {
  const prompt = buildJungianPrompt(answers);
  if (!prompt) return null;
  return generateJungianProfile(prompt, userTier);
}

/**
 * Full flow: get Jungian analysis text then generate partner cutout image via Replicate (google/nano-banana).
 * If image generation fails (e.g. high demand), still returns analysis so title and personality can be updated.
 * @param {Object} answers - Survey answers
 * @param {string} [userTier='free'] - User tier for rate limiting
 * @returns {Promise<{ analysis: string, imageUrl: string|null }|null>} Analysis text and cutout image URL (or null if image failed); or null if prompt is empty. Throws only if Jungian analysis API fails.
 */
export async function fetchJungianAnalysisAndCutout(answers, userTier = 'free') {
  const analysis = await fetchJungianAnalysis(answers, userTier);
  if (!analysis) return null;
  const lookingFor = answers?.looking_for ?? '';
  let imageUrl = null;
  try {
    imageUrl = await generatePartnerCutoutNanoBanana(analysis, userTier, lookingFor);
  } catch (err) {
    console.warn('Partner cutout image generation failed, keeping analysis for title/personality:', err?.message);
  }
  return { analysis, imageUrl };
}
